import { isInCheck } from '../movegen/attacks.js'
import { generateLegalMoves } from '../movegen/generate.js'
import { moveToSan, parseSan, type SanError } from '../notation/san.js'
import { moveToUci, parseUci, type UciError } from '../notation/uci.js'
import { INITIAL_FEN, parseFen, toFen, type FenError } from '../position/fen.js'
import { matchIntent, sameMove, type Move, type MoveIntent } from '../position/move.js'
import { makeMove, type Position } from '../position/position.js'
import { opposite, type Color } from '../primitives/color.js'
import { squareName } from '../primitives/square.js'
import { err, flatMap, ok, type Result } from '../result.js'
import { canMate, isInsufficientMaterial } from './material.js'
import { bumpKey, occurrences, repetitionKey } from './repetition.js'
import { isGameOver, type GameStatus } from './status.js'

/** Plies without a pawn move or capture before the game is drawn automatically. */
const SEVENTY_FIVE_MOVE_PLIES = 150
/** ...and the number at which a player may *claim* a draw. */
const FIFTY_MOVE_PLIES = 100

export interface HistoryEntry {
  readonly move: Move
  readonly san: string
  readonly uci: string
  /** The position this move produced. Entry `n - 1`'s `after` is its input. */
  readonly after: Position
  readonly key: string
}

export interface Game {
  readonly initial: Position
  readonly initialFen: string
  readonly history: readonly HistoryEntry[]
  readonly position: Position
  readonly status: GameStatus
  /** How often each position has occurred, for repetition claims. */
  readonly keyCounts: ReadonlyMap<string, number>
}

export type DrawClaim = 'threefoldRepetition' | 'fiftyMoveRule'

export type GameError =
  | { readonly kind: 'gameOver'; readonly status: GameStatus }
  | { readonly kind: 'illegalMove'; readonly move: Move }
  | { readonly kind: 'illegalIntent'; readonly intent: MoveIntent }
  | { readonly kind: 'claimUnavailable'; readonly claim: DrawClaim }
  | { readonly kind: 'notation'; readonly cause: SanError | UciError }

export const describeGameError = (error: GameError): string => {
  switch (error.kind) {
    case 'gameOver':
      return `the game is already over (${error.status.kind})`
    case 'illegalMove':
      return `${moveToUci(error.move)} is not legal in this position`
    case 'illegalIntent':
      return `${squareName(error.intent.from)}${squareName(error.intent.to)} is not legal in this position`
    case 'claimUnavailable':
      return `no ${error.claim} claim is available`
    case 'notation':
      return `unreadable move: ${error.cause.kind}`
  }
}

/**
 * Decide where a position stands, given how often it has been reached.
 *
 * Order matters: checkmate outranks every drawing rule, so a mate delivered on
 * the 150th quiet ply is a win rather than a draw.
 */
const adjudicate = (pos: Position, keyCounts: ReadonlyMap<string, number>, key: string): GameStatus => {
  if (generateLegalMoves(pos).length === 0) {
    return isInCheck(pos) ? { kind: 'checkmate', winner: opposite(pos.turn) } : { kind: 'stalemate' }
  }
  if (isInsufficientMaterial(pos)) return { kind: 'insufficientMaterial' }
  if (pos.halfmoves >= SEVENTY_FIVE_MOVE_PLIES) return { kind: 'seventyFiveMoveRule' }
  if (occurrences(keyCounts, key) >= 5) return { kind: 'fivefoldRepetition' }
  return { kind: 'playing', check: isInCheck(pos) }
}

export const newGame = (fen: string = INITIAL_FEN): Result<Game, FenError> =>
  flatMap(parseFen(fen), (initial) => {
    const key = repetitionKey(initial)
    const keyCounts = bumpKey(new Map(), key)
    return ok({
      initial,
      initialFen: toFen(initial),
      history: [],
      position: initial,
      status: adjudicate(initial, keyCounts, key),
      keyCounts,
    })
  })

export const legalMoves = (game: Game): readonly Move[] =>
  isGameOver(game.status) ? [] : generateLegalMoves(game.position)

export const turnOf = (game: Game): Color => game.position.turn

/** Draws a player may claim right now. Neither is applied automatically. */
export const availableClaims = (game: Game): readonly DrawClaim[] => {
  if (isGameOver(game.status)) return []
  const claims: DrawClaim[] = []
  const key = game.history.at(-1)?.key ?? repetitionKey(game.initial)
  if (occurrences(game.keyCounts, key) >= 3) claims.push('threefoldRepetition')
  if (game.position.halfmoves >= FIFTY_MOVE_PLIES) claims.push('fiftyMoveRule')
  return claims
}

export const applyMove = (game: Game, move: Move): Result<Game, GameError> => {
  if (isGameOver(game.status)) return err({ kind: 'gameOver', status: game.status })

  const legal = generateLegalMoves(game.position)
  const verified = legal.find((candidate) => sameMove(candidate, move))
  if (verified === undefined) return err({ kind: 'illegalMove', move })

  const after = makeMove(game.position, verified)
  const key = repetitionKey(after)
  const keyCounts = bumpKey(game.keyCounts, key)

  const entry: HistoryEntry = {
    move: verified,
    san: moveToSan(game.position, verified, legal),
    uci: moveToUci(verified),
    after,
    key,
  }

  return ok({
    ...game,
    history: [...game.history, entry],
    position: after,
    status: adjudicate(after, keyCounts, key),
    keyCounts,
  })
}

export const applyIntent = (game: Game, intent: MoveIntent): Result<Game, GameError> => {
  if (isGameOver(game.status)) return err({ kind: 'gameOver', status: game.status })
  const move = matchIntent(generateLegalMoves(game.position), intent)
  return move === null ? err({ kind: 'illegalIntent', intent }) : applyMove(game, move)
}

export const applySan = (game: Game, san: string): Result<Game, GameError> => {
  if (isGameOver(game.status)) return err({ kind: 'gameOver', status: game.status })
  const move = parseSan(game.position, san)
  return move.ok ? applyMove(game, move.value) : err({ kind: 'notation', cause: move.error })
}

export const applyUci = (game: Game, uci: string): Result<Game, GameError> => {
  if (isGameOver(game.status)) return err({ kind: 'gameOver', status: game.status })
  const move = parseUci(game.position, uci)
  return move.ok ? applyMove(game, move.value) : err({ kind: 'notation', cause: move.error })
}

const end = (game: Game, status: GameStatus): Result<Game, GameError> =>
  isGameOver(game.status) ? err({ kind: 'gameOver', status: game.status }) : ok({ ...game, status })

export const resign = (game: Game, color: Color): Result<Game, GameError> =>
  end(game, { kind: 'resignation', winner: opposite(color) })

export const agreeDraw = (game: Game): Result<Game, GameError> => end(game, { kind: 'agreement' })

/** Adjudicate a draw at a cost-motivated ply cap — see AI_MATCH_MOVE_CAP_PLIES. */
export const capMoves = (game: Game): Result<Game, GameError> => end(game, { kind: 'moveLimit' })

/**
 * `color` is the player whose clock hit zero. If the other side could not
 * possibly have mated — a bare king, or a king and one minor piece — the game
 * is drawn instead of lost, which is the rule people are most often surprised
 * by.
 */
export const flag = (game: Game, color: Color): Result<Game, GameError> => {
  const opponent = opposite(color)
  return end(
    game,
    canMate(game.position, opponent)
      ? { kind: 'timeout', winner: opponent }
      : { kind: 'timeoutVsInsufficientMaterial' },
  )
}

export const claimDraw = (game: Game, claim: DrawClaim): Result<Game, GameError> => {
  if (isGameOver(game.status)) return err({ kind: 'gameOver', status: game.status })
  if (!availableClaims(game).includes(claim)) return err({ kind: 'claimUnavailable', claim })
  return ok({ ...game, status: { kind: claim } })
}

/** Positions in order, for move-list scrubbing. Index 0 is before any move. */
export const positionAtPly = (game: Game, ply: number): Position =>
  ply <= 0 ? game.initial : (game.history[Math.min(ply, game.history.length) - 1]?.after ?? game.initial)
